import React, { useState, useEffect, useCallback } from 'react';
import { PiXLight, PiCaretLeftLight, PiCaretRightLight } from 'react-icons/pi';
import { GalleryImage, getYears, getCategories, filterImages } from '../data/galleryData';
import Reveal from '../components/Reveal';

interface ImageModalProps {
  image: GalleryImage;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ image, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = image.images || [image.url];

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  // Keyboard control + scroll lock while the lightbox is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNextImage();
      if (e.key === 'ArrowLeft') handlePrevImage();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, handleNextImage, handlePrevImage]);

  return (
    <div
      className="modal-overlay fixed inset-0 z-[60] flex items-center justify-center bg-espresso-900/80 p-4 backdrop-blur-3xl sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={image.title}
    >
      <div
        className="modal-content bezel-shell-dark relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bezel-core-dark overflow-hidden">
          <img
            src={images[currentImageIndex]}
            alt={image.title}
            className="modal-image max-h-[70vh] w-full rounded-squircle-inner object-contain"
          />
        </div>

        <button
          onClick={onClose}
          aria-label="Close"
          className="close-button absolute -top-3 -right-3 flex h-11 w-11 items-center justify-center rounded-full bg-cream text-espresso-800 shadow-ambient transition-all duration-700 ease-fluid hover:scale-105 active:scale-95"
        >
          <PiXLight className="h-5 w-5" strokeWidth={1} />
        </button>

        {images.length > 1 && (
          <>
            <div className="modal-navigation pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-3">
              <button
                onClick={handlePrevImage}
                aria-label="Previous image"
                className="nav-button prev-button pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full bg-cream/90 text-espresso-800 backdrop-blur-xl transition-all duration-700 ease-fluid hover:scale-105 active:scale-95"
              >
                <PiCaretLeftLight className="h-5 w-5" strokeWidth={1} />
              </button>
              <button
                onClick={handleNextImage}
                aria-label="Next image"
                className="nav-button next-button pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full bg-cream/90 text-espresso-800 backdrop-blur-xl transition-all duration-700 ease-fluid hover:scale-105 active:scale-95"
              >
                <PiCaretRightLight className="h-5 w-5" strokeWidth={1} />
              </button>
            </div>
            <p className="absolute -bottom-9 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-eyebrow text-cream/70">
              {currentImageIndex + 1} / {images.length}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const GalleryPage: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState(getYears()[0]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const years = getYears();
  const categories = getCategories(selectedYear);
  const filteredImages = filterImages(selectedYear, selectedCategory);

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setSelectedCategory('All');
  };

  return (
    <section className="gallery min-h-[100dvh] bg-mesh-warm px-4 pb-32 pt-36 sm:px-8 sm:pt-44 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <span className="eyebrow bg-sage-pale text-sage-deep">Graduates</span>
          <h1 className="mt-7 font-display text-[3rem] font-light leading-[0.98] tracking-[-0.035em] text-espresso-900 sm:text-7xl lg:text-8xl">
            Our Gallery
          </h1>
          <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-espresso-600">
            Celebrating our graduates and their achievements.
          </p>
        </Reveal>

        {/* Year tabs — a segmented island rather than underlined text */}
        <Reveal delay={100}>
          <div className="mt-14 flex flex-wrap items-center gap-3">
            <div className="year-tabs inline-flex rounded-full bg-espresso-700/[0.05] p-1 ring-1 ring-inset ring-espresso-700/[0.07]">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearChange(year)}
                  className={`year-tab rounded-full px-5 py-2 text-[13px] font-medium tracking-tight transition-all duration-700 ease-fluid ${
                    selectedYear === year
                      ? 'active bg-espresso-800 text-cream'
                      : 'text-espresso-600 hover:text-espresso-900'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Category filters */}
        <Reveal delay={160}>
          <div className="category-filters mt-4 flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`category-button rounded-full px-4 py-2 text-xs font-medium tracking-tight transition-all duration-700 ease-fluid ${
                  selectedCategory === category
                    ? 'active bg-sage text-white'
                    : 'bg-espresso-700/[0.04] text-espresso-600 ring-1 ring-inset ring-espresso-700/[0.07] hover:bg-espresso-700/[0.07]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Asymmetric bento gallery — every third tile spans wide on desktop */}
        <div className="gallery-grid mt-12 grid grid-cols-1 gap-4 md:grid-cols-12">
          {filteredImages.map((image, i) => (
            <Reveal
              key={image.id}
              delay={(i % 3) * 90}
              className={i % 3 === 0 ? 'md:col-span-7' : 'md:col-span-5'}
            >
              <button
                onClick={() => setSelectedImage(image)}
                className="gallery-item group block h-full w-full text-left"
                aria-label={`Open ${image.title}`}
              >
                <div className="bezel-shell h-full transition-all duration-700 ease-fluid hover:-translate-y-1 hover:shadow-lifted">
                  <div className="bezel-core flex h-full flex-col overflow-hidden">
                    <div className="overflow-hidden rounded-squircle-inner rounded-b-none">
                      <img
                        src={image.url}
                        alt={image.title}
                        loading="lazy"
                        className="gallery-image h-64 w-full object-cover transition-transform duration-1200 ease-fluid group-hover:scale-[1.05] sm:h-80"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-7">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="font-display text-2xl font-light tracking-tight text-espresso-900">
                          {image.title}
                        </h3>
                        <span className="category-tag eyebrow shrink-0 bg-sage-pale text-sage-deep">
                          {image.category}
                        </span>
                      </div>
                      {image.testimony && (
                        <p className="testimony mt-4 font-display text-[15px] font-light italic leading-relaxed text-espresso-600">
                          “{image.testimony}”
                        </p>
                      )}
                      {image.images && image.images.length > 1 && (
                        <p className="mt-6 text-[10px] uppercase tracking-eyebrow text-espresso-500">
                          {image.images.length} photos
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {filteredImages.length === 0 && (
          <p className="mt-16 text-sm text-espresso-500">
            No photographs in this category yet.
          </p>
        )}
      </div>

      {selectedImage && (
        <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />
      )}
    </section>
  );
};

export default GalleryPage;
